// INTELIMOVE-ARQ-005A — opportunity-radar/1. Regras puras: nenhuma consulta, escrita, pesquisa ou IA.
import { z } from 'zod';
import type { EvidenceRef } from './evidence.js';
import type { OpportunityAssessment } from './opportunity.js';

export const OPPORTUNITY_RADAR_VERSION = 'opportunity-radar/1' as const;
export const RADAR_EVENT_STATES = ['NOVO', 'ATUALIZACAO_RELEVANTE', 'DESATUALIZADO', 'CONFLITO'] as const;
export type RadarEventState = (typeof RADAR_EVENT_STATES)[number];
export const RADAR_RELEVANCE = ['ATENCAO_PRIORITARIA', 'INVESTIGAR', 'MUDANCA_RELEVANTE', 'INFORMATIVA', 'DADOS_INSUFICIENTES'] as const;
export type RadarRelevance = (typeof RADAR_RELEVANCE)[number];
export const RADAR_RELEVANCE_LABEL: Record<RadarRelevance, string> = {
  ATENCAO_PRIORITARIA: 'Atenção prioritária', INVESTIGAR: 'Oportunidade para investigar', MUDANCA_RELEVANTE: 'Mudança relevante',
  INFORMATIVA: 'Atualização informativa', DADOS_INSUFICIENTES: 'Dados insuficientes',
};
export type RadarEventKind = 'TARGET_SIGNAL' | 'COMPETITOR_SIGNAL' | 'MARKET_SIGNAL' | 'COMMERCIAL_EVIDENCE' | 'PAGE_CHANGE' | 'BATTLECARD_CHANGE';
export type RadarEntity = { kind: 'EMPRESA_ALVO' | 'CONCORRENTE' | 'FONTE'; id: string | null; name: string };
export type RadarAction = { key: 'ANALISAR_EMPRESA' | 'REVISAR_OPORTUNIDADE' | 'CONSULTAR_CONCORRENTE' | 'INVESTIGAR_SINAL' | 'REVISAR_ESTRATEGIA' | 'CONFIGURAR_ACOMPANHAMENTO'; label: string; href: string };

export interface RadarCandidate {
  id: string;
  dedupeKey: string;
  kind: RadarEventKind;
  state: RadarEventState;
  entity: RadarEntity;
  targetId: string | null;
  occurredAt: string;
  collectedAt: string;
  validUntil: string | null;
  title: string;
  change: string;
  commercialReason: string | null;
  source: { id: string | null; name: string | null; url: string | null };
  evidence: EvidenceRef[];
  confidence: 'ALTA' | 'MEDIA' | 'BAIXA' | null;
  material: boolean;
  current: boolean | null;
  assessment: OpportunityAssessment | null;
  action: RadarAction;
}

export interface RadarEvent extends Omit<RadarCandidate, 'dedupeKey' | 'material' | 'current' | 'assessment'> {
  contractVersion: typeof OPPORTUNITY_RADAR_VERSION;
  relevance: RadarRelevance;
  relevanceLabel: string;
  whyItMatters: string;
  opportunityImpact: { targetId: string; classification: OpportunityAssessment['classification']; classificationLabel: string; summary: string } | null;
  limitations: string[];
}

export interface OpportunityRadar {
  contractVersion: typeof OPPORTUNITY_RADAR_VERSION;
  generatedAt: string;
  monitoring: { configured: boolean; activeSources: number; activeSchedules: number; lastRunAt: string | null };
  items: RadarEvent[];
  total: number;
  limit: number;
  offset: number;
}

export const OpportunityRadarQuery = z.object({
  limit: z.coerce.number().int().min(1).max(50).default(5),
  offset: z.coerce.number().int().min(0).max(10_000).default(0),
}).strict();

const rank: Record<RadarRelevance, number> = { ATENCAO_PRIORITARIA: 0, INVESTIGAR: 1, MUDANCA_RELEVANTE: 2, INFORMATIVA: 3, DADOS_INSUFICIENTES: 4 };
const uniqueEvidence = (sets: readonly EvidenceRef[][]) => [...new Map(sets.flat().map((e) => [e.id, e])).values()];
const conflict = (c: RadarCandidate) => c.state === 'CONFLITO' || c.evidence.some((e) => e.contradictory || e.informationState === 'CONFLITANTE');
const usefulEvidence = (c: RadarCandidate) => c.evidence.some((e) => e.informationState !== 'INDISPONIVEL');

export function classifyRadarCandidate(c: RadarCandidate): RadarRelevance {
  if (!usefulEvidence(c)) return 'DADOS_INSUFICIENTES';
  if (c.state === 'DESATUALIZADO' || c.current === false) return 'DADOS_INSUFICIENTES';
  if (conflict(c)) return 'MUDANCA_RELEVANTE';
  if (c.kind === 'TARGET_SIGNAL' && c.material && c.assessment && ['PRIORITARIA', 'PROMISSORA', 'EM_AVALIACAO'].includes(c.assessment.classification)) return 'ATENCAO_PRIORITARIA';
  if (c.kind === 'TARGET_SIGNAL' || c.kind === 'MARKET_SIGNAL') return 'INVESTIGAR';
  if (c.kind === 'COMMERCIAL_EVIDENCE' && c.assessment) return 'INVESTIGAR';
  if (c.material || c.kind === 'BATTLECARD_CHANGE') return 'MUDANCA_RELEVANTE';
  return 'INFORMATIVA';
}

function why(c: RadarCandidate, relevance: RadarRelevance): string {
  if (conflict(c)) return 'Há informações incompatíveis; confirme a evidência antes de decidir.';
  if (c.state === 'DESATUALIZADO' || c.current === false) return 'A informação perdeu a validade e precisa ser reavaliada.';
  if (relevance === 'ATENCAO_PRIORITARIA' && c.assessment) return `A mudança pode afetar uma empresa hoje classificada como ${c.assessment.classificationLabel.toLowerCase()}.`;
  if (c.commercialReason) return c.commercialReason;
  if (c.kind === 'COMPETITOR_SIGNAL' || c.kind === 'BATTLECARD_CHANGE') return 'A mudança pode alterar a leitura competitiva ou a estratégia comercial.';
  if (c.kind === 'PAGE_CHANGE') return 'A fonte acompanhada registrou uma mudança comercialmente relevante.';
  return 'A informação pode contribuir para a avaliação comercial e merece investigação.';
}

function limitations(c: RadarCandidate): string[] {
  const out: string[] = [];
  if (!c.validUntil) out.push('A validade da informação não foi determinada.');
  if (!c.confidence) out.push('A confiança não foi informada pela origem.');
  if (c.evidence.some((e) => e.informationState === 'INFERIDA')) out.push('Parte da leitura é inferida e requer confirmação.');
  if (conflict(c)) out.push('As evidências contêm conflito não resolvido.');
  if (c.assessment?.dataCoverage.preliminary) out.push('A avaliação comercial é preliminar por cobertura limitada.');
  return [...new Set(out)];
}

/** Deduplica pela identidade comercial, preserva a leitura mais relevante/recente e reúne referências originais. */
export function buildOpportunityRadar(candidates: readonly RadarCandidate[], input: { now: string; limit: number; offset: number; monitoring: OpportunityRadar['monitoring'] }): OpportunityRadar {
  const byKey = new Map<string, RadarCandidate>();
  for (const c of candidates) {
    const prev = byKey.get(c.dedupeKey);
    if (!prev) { byKey.set(c.dedupeKey, c); continue; }
    const cr = classifyRadarCandidate(c), pr = classifyRadarCandidate(prev);
    const chosen = rank[cr] < rank[pr] || (rank[cr] === rank[pr] && c.occurredAt > prev.occurredAt) ? c : prev;
    byKey.set(c.dedupeKey, { ...chosen, evidence: uniqueEvidence([prev.evidence, c.evidence]) });
  }
  const all = [...byKey.values()].map((c): RadarEvent => {
    const relevance = classifyRadarCandidate(c);
    const { dedupeKey: _d, material: _m, current: _c, assessment, ...base } = c;
    void _d; void _m; void _c;
    return { ...base, contractVersion: OPPORTUNITY_RADAR_VERSION, relevance, relevanceLabel: RADAR_RELEVANCE_LABEL[relevance], whyItMatters: why(c, relevance),
      opportunityImpact: assessment && c.targetId ? { targetId: c.targetId, classification: assessment.classification, classificationLabel: assessment.classificationLabel, summary: assessment.summary } : null,
      limitations: limitations(c) };
  }).sort((a, b) => rank[a.relevance] - rank[b.relevance] || b.occurredAt.localeCompare(a.occurredAt) || a.id.localeCompare(b.id));
  return { contractVersion: OPPORTUNITY_RADAR_VERSION, generatedAt: input.now, monitoring: input.monitoring, items: all.slice(input.offset, input.offset + input.limit), total: all.length, limit: input.limit, offset: input.offset };
}
